import { useState, useRef } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";

export default function ImageUploader({ 
  value, 
  onChange, 
  category = "thumbnails",
  stickerType = "sticker", // "sticker" (512x512) or "tray" (96x96)
  placeholder = "https://... o sube una imagen",
  recommendedSize = ""
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Tipo de archivo no permitido. Usa JPG, PNG, GIF o WebP");
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Archivo muy grande. Máximo 10MB");
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);

      // Build URL with query params
      let url = `/uploads/image?category=${category}`;
      if (category === 'stickers') {
        url += `&sticker_type=${stickerType}`;
      }

      const response = await adminApi.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        // Use the full URL for the image
        const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
        onChange(imageUrl);
        
        // Show appropriate success message
        if (response.data.converted) {
          const size = stickerType === 'tray' ? '96x96' : '512x512';
          toast.success(`Imagen convertida a WebP ${size}`);
        } else {
          toast.success("Imagen subida correctamente");
        }
      }
    } catch (error) {
      console.error("Upload error:", error);
      const errorMsg = error.response?.data?.detail || "Error al subir la imagen";
      toast.error(errorMsg);
    } finally {
      setIsUploading(false);
    }
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const clearImage = () => {
    onChange("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      {/* URL Input */}
      <div className="flex gap-2">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="bg-gray-50 border-gray-200 text-black focus:border-[#E91E8C] flex-1"
        />
        {value && (
          <Button 
            type="button" 
            variant="ghost" 
            size="icon"
            onClick={clearImage}
            className="text-gray-400 hover:text-red-500"
          >
            <X size={18} />
          </Button>
        )}
      </div>

      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-4 transition-colors cursor-pointer ${
          dragOver 
            ? 'border-[#E91E8C] bg-pink-50' 
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handleInputChange}
          className="hidden"
        />

        {isUploading ? (
          <div className="flex items-center justify-center gap-2 text-gray-500">
            <Loader2 size={20} className="animate-spin" />
            <span className="text-sm">Subiendo...</span>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 text-gray-500">
            <Upload size={20} />
            <span className="text-sm">
              Arrastra una imagen o haz clic para seleccionar
            </span>
          </div>
        )}
      </div>

      {/* Recommended size hint */}
      {recommendedSize && (
        <p className="text-xs text-gray-400">{recommendedSize}</p>
      )}

      {/* Preview */}
      {value && (
        <div className="mt-2">
          <img 
            src={value} 
            alt="Preview" 
            className="max-h-32 rounded-lg border border-gray-200 object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
      )}
    </div>
  );
}
